import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Backdrop, CircularProgress } from '@mui/material'

import { user } from 'ampersand-app'
import ThemeInterface from '@/theme-interface'
import { closeTenantSelection } from '@/redux/ducks/tenant/selection'
import DrawerWithOffset from '@/components/drawers/drawer-with-offset'
import { ProductTypes, ROLES } from '@/modules/base/util/constants'
import TenantSelectionSwitchItem from '@/modules/base/components/tenant-selection/items/tenant-selection-switch-item'
import TenantSelectionCreateItem from '@/modules/base/components/tenant-selection/items/tenant-selection-create-item'
import TenantSelectionHeader from '@/modules/base/components/tenant-selection/tenant-selection-header'
import { ColorModes } from '@/modules/layout/constants'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  wrapper: {
    maxWidth: '100%',
  },
  children: {
    padding: 0,
  },
  content: {
    display: 'grid',
    gridTemplateAreas: '"header" "items" "footer"',
    gridTemplateRows: 'auto 1fr auto',
    height: '100%',
  },
  header: {
    gridArea: 'header',
  },
  items: {
    gridArea: 'items',
    overflow: 'auto',
    backgroundColor: theme.mode.backgroundColor.default,
    color: theme.mode.color.default,
  },
  seperator: {
    borderTop: `1px solid ${theme.mode.borderColor.medium}`,
    opacity: theme.mode.type === ColorModes.dark ? 0.2 : 1.0,
  },
  footer: {
    gridArea: 'footer',
    boxShadow: theme.mode.boxShadow.default,
    backgroundColor: theme.mode.backgroundColor.default,
    color: theme.mode.color.default,
  },
  backdrop: {
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  },
}), { name: 'TenantSelectionDrawer' })

const TenantSelectionDrawerContent = () => {
  const classes = useStyles()
  const theme = useTheme<ThemeInterface>()
  const dispatch = useDispatch()
  const { open, isLoading } = useSelector(state => state.tenant.selection)
  const defaultUserInfo = useSelector(state => state.user.info)
  const [userInfo, setUserInfo] = useState(defaultUserInfo)
  const { activeTenant, tenants, roles } = userInfo
  const isUserTaxConsultant = roles?.includes(ROLES.TAX_CONSULTANT_ROLE)

  useEffect(() => {
    // unfortunately we cannot use redux at the moment as it redraws the complete app
    // and we need latest user info
    // and we cannot upate redux as this updates ampersand and redraws the complete app
    // also see account-button line 377
    if (!open) { return }

    user.getUserInfo()
      .then(({ body: { data: { userRoles: roles, ...rest } } }) => {
        setUserInfo({ ...rest, roles })
      })
  }, [open, defaultUserInfo])

  const styles = {
    wrapper: classes.wrapper,
    children: classes.children,
  }
  const { tenantId } = activeTenant

  // create repo with tenants and demo tenants
  const tenantRepo = useMemo(() => {
    const defaultRepo = {
      tenants: [],
      taxOfficeTenant: null,
      demoTenant: null,
    }

    return tenants.reduce((tenantRepo, tenant) => {
      const { isDemo, subscriptions, isOwner } = tenant
      const taxConsultantSub = subscriptions.find((sub) => sub.type === ProductTypes.TAX_CONSULTANT)
      if (((isUserTaxConsultant || isOwner) && taxConsultantSub)) {
        tenantRepo.taxOfficeTenant = tenant
        return tenantRepo
      }
      if (tenantId === tenant.tenantId) { return tenantRepo }
      if (isDemo) {
        tenantRepo.demoTenant = tenant
        return tenantRepo
      }
      tenantRepo.tenants.push(tenant)
      return tenantRepo
    }, defaultRepo)
  }, [userInfo])

  return (
    <>
      <Backdrop open={isLoading} classes={{ root: classes.backdrop }}>
        <CircularProgress color='primary' />
      </Backdrop>
      <DrawerWithOffset
        dataQsId='tenantSelectionDrawer'
        withoutScrolling
        anchor='right'
        open={open}
        styles={styles}
        onClose={() => dispatch(closeTenantSelection())}>
        <div className={classes.content}>
          <div className={classes.header}>
            <TenantSelectionHeader
              showBackToTaxOffice={!!tenantRepo.taxOfficeTenant && activeTenant.tenantId !== tenantRepo.taxOfficeTenant?.tenantId}
              companyName={activeTenant.companyName}
              taxOfficeTenantId={tenantRepo.taxOfficeTenant?.tenantId} />
          </div>
          <div className={classes.items}>
            <PerfectScrollbar options={{ scrollXMarginOffset: 10 }}>
              {tenantRepo.tenants.map((tenant, index) => {
                const { tenantId, companyName, tenantName, color, backgroundColor, tenantRestores } = tenant
                const hideSeperator = tenantRepo.tenants.length > 1 && index === (tenantRepo.tenants.length - 1)
                return (
                  <TenantSelectionSwitchItem
                    key={tenantId}
                    hideSeperator={hideSeperator}
                    tenantId={tenantId}
                    companyName={companyName}
                    tenantName={tenantName}
                    tenantRestores={tenantRestores}
                    iconColors={{ color, backgroundColor }} />
                )
              })}
            </PerfectScrollbar>
          </div>
          <div className={classes.seperator} />
          <div className={classes.footer}>
            <TenantSelectionCreateItem isDemo={false} />
            {tenantRepo.demoTenant
              ? (
                <TenantSelectionSwitchItem
                  isDemo
                  hideSeperator
                  key={tenantRepo.demoTenant.tenantId}
                  tenantId={tenantRepo.demoTenant.tenantId}
                  companyName={tenantRepo.demoTenant.companyName}
                  tenantName={tenantRepo.demoTenant.tenantName}
                  iconColors={{ color: theme.white, backgroundColor: theme.iconColor }} />
              )
              : <TenantSelectionCreateItem isDemo createDemo hideSeperator />
            }
          </div>
        </div>
      </DrawerWithOffset>
    </>
  )
}

export default TenantSelectionDrawerContent
